import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { Section, Card, CheckList, FaqList } from "../components/landing/Blocks";
import { findCaseStudy } from "../data/caseStudies";
import { applySeo } from "../lib/seo";
import NotFoundPage from "./NotFoundPage";

const Stats = ({ stats }) => (
  <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="case-stats">
    {stats.map(([v, l]) => (
      <Card key={l} className="!p-5">
        <div className="font-condensed text-[var(--ggm-signal-cyan)]" style={{ fontSize: "clamp(1.6rem, 3.6vw, 2.4rem)" }}>{v}</div>
        <div className="mt-1 font-condensed uppercase tracking-widest text-xs text-white/60">{l}</div>
      </Card>
    ))}
  </div>
);

const ResultsTable = ({ rows }) => (
  <div className="overflow-x-auto rounded-md border" style={{ borderColor: "var(--ggm-border-subtle)" }}>
    <table className="w-full text-left font-body text-sm md:text-base" data-testid="case-results-table">
      <thead style={{ background: "var(--ggm-surface)" }}>
        <tr className="font-condensed uppercase tracking-widest text-xs text-white/60">
          <th className="p-4">Measure</th><th className="p-4">Before (Oct 20)</th><th className="p-4">After (Oct 21)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]} className="border-t" style={{ borderColor: "var(--ggm-border-subtle)" }}>
            <td className="p-4 text-white/85 min-w-[200px]">{r[0]}</td>
            <td className="p-4 text-white/55 whitespace-nowrap">{r[1]}</td>
            <td className="p-4 text-[var(--ggm-signal-cyan)] font-bold min-w-[160px]">{r[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Hero = ({ c }) => (
  <section className="max-w-6xl mx-auto px-5 md:px-8" style={{ paddingTop: "clamp(40px, 7vh, 88px)" }} data-testid="case-hero">
    <Link to="/case-studies" className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/60 hover:text-[var(--ggm-signal-cyan)]" data-testid="case-back-link">
      <ArrowLeft size={14} /> Case Studies
    </Link>
    <div className="ggm-eyebrow mt-6 mb-5">Case Study · Healthcare · Gallatin, TN</div>
    <h1 className="font-condensed uppercase text-white leading-[1.02] max-w-5xl" style={{ fontSize: "clamp(2rem, 5vw, 3.9rem)", letterSpacing: "-0.015em" }} data-testid="case-h1">
      {c.h1}
    </h1>
    <p className="mt-6 font-body text-white/80 text-base md:text-lg leading-relaxed max-w-3xl"><b className="text-white">In short:</b> {c.inShort}</p>
    <dl className="mt-6 grid gap-2 font-body text-sm text-white/70 max-w-3xl" data-testid="case-facts">
      {c.facts.map(([k, v]) => (
        <div key={k}><dt className="inline font-bold text-white">{k}: </dt><dd className="inline">{v}</dd></div>
      ))}
    </dl>
    <Stats stats={c.stats} />
  </section>
);

export default function CaseStudyPage() {
  const { slug } = useParams();
  const c = findCaseStudy(slug);
  useEffect(() => (c ? applySeo(c) : undefined), [c]);
  if (!c) return <NotFoundPage />;

  return (
    <PageShell testId="case-study-page">
      <Hero c={c} />
      <Section eyebrow="The challenge" title="Patients were slipping away" testId="case-challenge">
        <p className="font-body text-white/75 mb-6 max-w-3xl">{c.challengeIntro}</p>
        <CheckList items={c.challenge} />
      </Section>
      <Section eyebrow="What we did" title="Audit, front desk, fixes" testId="case-work">
        <div className="grid gap-6">
          <Card testId="case-step-audit">
            <h3 className="font-condensed uppercase text-white text-xl">1. Free Local AI Audit</h3>
            <p className="mt-3 font-body text-white/75 leading-relaxed">{c.auditStep}</p>
            <Link to="/audit" className="mt-3 inline-block underline text-white hover:text-[var(--ggm-signal-cyan)] text-sm" data-testid="case-audit-link">About the free audit</Link>
          </Card>
          <Card testId="case-step-front-desk">
            <h3 className="font-condensed uppercase text-white text-xl">2. AI Front Desk</h3>
            <p className="mt-3 font-body text-white/75 leading-relaxed">{c.frontDesk}</p>
            <p className="mt-5 mb-3 font-body text-white/75">{c.privacyIntro}</p>
            <CheckList items={c.privacy} testId="case-privacy" />
          </Card>
          <Card testId="case-step-fixes">
            <h3 className="font-condensed uppercase text-white text-xl mb-4">3. Website fixes</h3>
            <CheckList items={c.fixes} />
          </Card>
        </div>
      </Section>
      <Section eyebrow="Results" title="Before and after" testId="case-results">
        <ResultsTable rows={c.results} />
        <p className="mt-5 font-body text-white/60 text-sm max-w-3xl">{c.resultsNote}</p>
      </Section>
      <Section eyebrow="Why it matters" title="Why it matters for local practices" testId="case-why">
        <div className="space-y-4 max-w-3xl">{c.why.map((p) => <p key={p} className="font-body text-white/80 leading-relaxed">{p}</p>)}</div>
      </Section>
      <Section eyebrow="Questions, answered" title="FAQ" testId="case-faq"><FaqList faqs={c.faqs} testId="case-faq-list" /></Section>
      <Section eyebrow="Your practice next" title="Want the same for your practice?" testId="case-cta">
        <p className="font-body text-white/80 max-w-3xl leading-relaxed">{c.ctaText}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/audit" className="ggm-btn-primary" data-testid="case-cta-audit">Get my free audit <ArrowUpRight size={16} /></Link>
          <Link to="/front-office" className="ggm-btn-secondary" data-testid="case-cta-front-office">See the AI Front Desk <ArrowRight size={16} /></Link>
          <Link to={`/projects/${c.projectSlug}`} className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-sm text-white/70 hover:text-[var(--ggm-signal-cyan)]" data-testid="case-project-link">See the project</Link>
        </div>
      </Section>
    </PageShell>
  );
}
